import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ApolloProvider, ApolloClient, InMemoryCache, from, ApolloLink, createHttpLink } from "@apollo/client";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getToken, getUserProfile, setToken } from "./jwt";
import Home from "./pages/Home";
import SignIn from "./pages/SignIn";
import Support from "./pages/Support";
import Chat from "./pages/Chat";
import "@fontsource/inter";
import env from 'env';

const uploadLink = createUploadLink({
  uri: env.GRAPHQL_URI || 'https://api.trafica.co.za/graphql',
  credentials: 'same-origin'
});

const requestHeaderLink = new ApolloLink((operation, forward) => {
  let token = null;

  if (isChatPage()) {
    // For chat page, only use URL token (support ticket token), no JWT
    token = getTokenFromUrl();
  } else if (isSupportPage()) {
    // For support page, use URL token if available, otherwise JWT token from localStorage
    token = getTokenFromUrl() || getToken();
  } else {
    // For other pages, use JWT token from localStorage
    token = getToken();
  }

  if (token) {
    operation.setContext({
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  return forward(operation);
});

const responseHeaderLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    const context = operation.getContext();
    const headers = context.response?.headers;

    if (headers) {
      const authHeader = headers.get('Authorization');
      if (authHeader) {
        setToken(authHeader.replace('Bearer ', ''));
      }
    }

    return response;
  });
});

const client = new ApolloClient({
  link: from([requestHeaderLink, responseHeaderLink, uploadLink]),
  cache: new InMemoryCache(),
});

const getTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('token');
};

const isSupportPage = () => {
  return window.location.hostname === 'support.trafica.co.za';
};

const isChatPage = () => {
  return window.location.pathname.startsWith('/chat');
};

const App = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [urlToken, setUrlToken] = useState(null);

  useEffect(() => {
    if (isSupportPage()) {
      // For support page, check if there's a token in URL
      const token = getTokenFromUrl();
      if (token) {
        setUrlToken(token);
      }
    }
    // Always try to get user profile from localStorage for all pages
    setUserProfile(getUserProfile());
  }, []);

  // Public chat page - no authentication required
  if (isChatPage()) {
    return <Chat />;
  }

  // Support page - use URL token if available, otherwise JWT from localStorage
  if (isSupportPage()) {
    return <Support token={urlToken} />;
  }

  if (userProfile) {
    return <Home />;
  }

  return <SignIn />;
};

document.addEventListener("DOMContentLoaded", () => {
  const root = document.getElementById("root");
  if (root) {
    ReactDOM.createRoot(root).render(
      <ApolloProvider client={client}>
        <App />
      </ApolloProvider>
    );
  }
});
